import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import toast from "react-hot-toast";
import { Plus, Trash2 } from "lucide-react";
import { deleteRoute, listRoutes, type RouteAssigneeFilter, type RouteRow, type RouteStatus } from "@/services/routes";
import { ROUTE_STATUSES } from "@myroute/shared";
import { listTeamMembers } from "@/services/team";
import { formatMiles } from "@/lib/distance";
import { useAuthStore } from "@/store/auth";
import { useIsAdmin, useIsFullAdmin } from "@/hooks/useCurrentUser";
import { nextSelectionFromClick, pageSelectionState, togglePageSelection } from "@/lib/selection";

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

const STATUS_STYLES: Record<RouteStatus, string> = {
  draft: "bg-slate-100 text-slate-700",
  published: "bg-amber-50 text-amber-700",
  in_progress: "bg-sky-50 text-sky-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
};

function StatusBadge({ status }: { status: RouteStatus }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[status]}`}>
      {status.replace("_", " ")}
    </span>
  );
}

// Filter persistence is scoped per-team so admins switching tenants don't
// drag filter state between orgs.
const DRIVER_FILTER_KEY_PREFIX = "myroute:routes:driverFilter:v1:";
function readStoredDriverFilter(key: string): RouteAssigneeFilter {
  if (typeof window === "undefined") return "all";
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return "all";
    // Stored as either a uuid (specific driver) or "all" | "pool" | "unassigned".
    return raw as RouteAssigneeFilter;
  } catch {
    return "all";
  }
}

export function RoutesAll() {
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  // Driver/status filter bar visible to admin OR route_manager (both have
  // team-wide visibility per the elevated-permission bucket).
  const isAdmin = useIsAdmin();
  // Hard route deletion = admin_user only (routes_delete RLS, migration 0033).
  const isFullAdmin = useIsFullAdmin();
  const qc = useQueryClient();
  const [urlParams, setUrlParams] = useSearchParams();

  // Admin-only driver filter. Non-admins are already RLS-scoped to their own
  // routes + pool, so the dropdown adds no value (and we don't render it).
  const driverFilterKey = useMemo(
    () => `${DRIVER_FILTER_KEY_PREFIX}${me?.teamId ?? "anon"}`,
    [me?.teamId],
  );
  // Dashboard click-through writes ?driver=pool or ?assignedTo=<uuid> when
  // drilling from the KPI cards into the routes list. These override the
  // persisted filter on this initial render only — without this branch the
  // user would land back on whatever they last filtered to manually.
  const urlDriver = urlParams.get("driver");
  const urlAssignedTo = urlParams.get("assignedTo");
  const urlStatusFilter = urlParams.get("status");
  const initialFilter: RouteAssigneeFilter = useMemo(() => {
    if (urlDriver === "pool" || urlDriver === "unassigned" || urlDriver === "all") return urlDriver;
    if (urlAssignedTo) return urlAssignedTo;
    return readStoredDriverFilter(driverFilterKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // one-shot: mount-time only
  const [driverFilter, setDriverFilter] = useState<RouteAssigneeFilter>(initialFilter);

  // Lifecycle-status filter (draft/published/in_progress/completed/cancelled).
  // Picked up from ?status= on mount (dashboard drill-down), then a regular
  // dropdown the user can change. Not persisted to localStorage because the
  // dashboard click sets it transiently — persisting would surprise the user
  // ("why does Routes/All keep filtering to in_progress days later?").
  const [statusFilter, setStatusFilter] = useState<RouteStatus | "all">(() => {
    if (urlStatusFilter && (ROUTE_STATUSES as readonly string[]).includes(urlStatusFilter)) {
      return urlStatusFilter as RouteStatus;
    }
    return "all";
  });

  // Drop the URL params once they've been folded into local state so the back
  // button doesn't re-apply them.
  useEffect(() => {
    if (urlDriver || urlAssignedTo || urlStatusFilter) {
      setUrlParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (driverFilter == null || driverFilter === "all") {
      window.localStorage.removeItem(driverFilterKey);
    } else {
      window.localStorage.setItem(driverFilterKey, driverFilter);
    }
  }, [driverFilterKey, driverFilter]);

  const q = useQuery({
    queryKey: ["routes", "list", { driverFilter, statusFilter }],
    queryFn: () =>
      listRoutes({
        assignedTo: driverFilter,
        status: statusFilter === "all" ? null : statusFilter,
      }),
  });
  const membersQuery = useQuery({
    queryKey: ["team", "members"],
    queryFn: listTeamMembers,
    enabled: isAdmin,
    staleTime: 5 * 60_000,
  });

  // Self-heal a stale persisted filter: if the previously-stored driver id
  // no longer exists in the team (off-boarded, deactivated, etc.), reset to
  // "all" so the dropdown matches a real option and the listRoutes query
  // doesn't quietly filter to a ghost driver. "pool" / "unassigned" /
  // "all" are never stale — only real uuids need this check.
  useEffect(() => {
    if (!membersQuery.data) return;
    if (driverFilter == null || driverFilter === "all" || driverFilter === "pool" || driverFilter === "unassigned") return;
    const stillThere = membersQuery.data.some((m) => m.id === driverFilter);
    if (!stillThere) {
      setDriverFilter("all");
    }
  }, [membersQuery.data, driverFilter]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(25);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedIdRef = useRef<string | null>(null);

  const rows: RouteRow[] = useMemo(() => q.data ?? [], [q.data]);
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageRows = useMemo(
    () => rows.slice(page * pageSize, page * pageSize + pageSize),
    [rows, page, pageSize],
  );
  const pageIds = useMemo(() => pageRows.map((r) => r.id), [pageRows]);
  const pageSelState = pageSelectionState(pageIds, selectedIds);

  function handleRowClick(id: string, shiftKey: boolean) {
    setSelectedIds((prev) => nextSelectionFromClick(id, shiftKey, pageIds, lastClickedIdRef.current, prev));
    lastClickedIdRef.current = id;
  }

  function handlePageToggle() {
    setSelectedIds((prev) => togglePageSelection(pageIds, prev));
    lastClickedIdRef.current = null;
  }

  function clearSelection() {
    setSelectedIds(new Set());
    lastClickedIdRef.current = null;
  }

  const deleteMut = useMutation({
    // Sequential per-route deletes so the 0035 worked-route guard can block
    // individual routes without failing the whole batch.
    mutationFn: async (ids: string[]) => {
      let deleted = 0;
      let blocked = 0;
      for (const id of ids) {
        try {
          await deleteRoute(id);
          deleted++;
        } catch (e) {
          if (e instanceof Error && e.message.includes("route_has_history")) blocked++;
          else throw e;
        }
      }
      return { deleted, blocked };
    },
    onSuccess: ({ deleted, blocked }) => {
      if (deleted > 0) toast.success(`Deleted ${deleted} route${deleted === 1 ? "" : "s"}`);
      if (blocked > 0) {
        toast.error(
          `${blocked} route${blocked === 1 ? " has" : "s have"} recorded visit outcomes — cancel ${blocked === 1 ? "it" : "those"} instead so the history is preserved.`,
          { duration: 6000 },
        );
      }
      clearSelection();
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["routes"] });
      // Deleted routes release their leads back into the mappable pool.
      void qc.invalidateQueries({ queryKey: ["leads"] });
    },
  });

  function confirmDeleteSelected() {
    if (selectedIds.size === 0 || deleteMut.isPending) return;
    const label = selectedIds.size === 1 ? "this route" : `${selectedIds.size} routes`;
    if (!confirm(`Delete ${label}? This is permanent. Routes with recorded visit outcomes will be skipped — cancel those instead.`)) return;
    deleteMut.mutate([...selectedIds]);
  }

  if (q.isLoading) return <p className="text-sm text-slate-500">Loading routes…</p>;
  if (q.error) return <p className="text-sm text-rose-700">Failed to load routes.</p>;

  const filterActive = isAdmin && ((driverFilter !== "all" && driverFilter !== null) || statusFilter !== "all");
  const activeMembers = (membersQuery.data ?? []).filter((m) => m.isActive);

  function setFilterAndResetPage(next: RouteAssigneeFilter) {
    setDriverFilter(next);
    setPage(0);
    setSelectedIds(new Set());
    lastClickedIdRef.current = null;
  }
  function setStatusAndResetPage(next: RouteStatus | "all") {
    setStatusFilter(next);
    setPage(0);
    setSelectedIds(new Set());
    lastClickedIdRef.current = null;
  }

  // Active-filter empty state: don't show the "Plan a route" hero when the
  // user has just narrowed to a slice that happens to be empty — surface a
  // "clear filter" affordance instead so they can recover in one click.
  if (rows.length === 0 && !filterActive) {
    return (
      <div className="space-y-3">
        {isAdmin && <FilterBar driverFilter={driverFilter} onChange={setFilterAndResetPage} statusFilter={statusFilter} onStatusChange={setStatusAndResetPage} members={activeMembers} />}
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
          <p className="text-sm font-medium text-slate-700">No routes yet</p>
          <p className="mt-1 text-xs text-slate-500">
            {isAdmin
              ? "Plan your first route to dispatch a driver."
              : "Routes will appear here once an admin assigns one to you or drops one in the team pool."}
          </p>
          {isAdmin && (
            <Link
              to="/routes/plan"
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" /> Plan a route
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {isAdmin && <FilterBar driverFilter={driverFilter} onChange={setFilterAndResetPage} statusFilter={statusFilter} onStatusChange={setStatusAndResetPage} members={activeMembers} />}

      {rows.length === 0 && filterActive && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
          <p className="text-sm font-medium text-slate-700">No routes match this filter.</p>
          <button
            type="button"
            onClick={() => setFilterAndResetPage("all")}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Show all routes
          </button>
        </div>
      )}

      {isAdmin && selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <span className="text-slate-700">
            <span className="font-semibold">{selectedIds.size}</span> selected
          </span>
          <div className="flex items-center gap-3">
            {isFullAdmin && (
              <button
                type="button"
                onClick={confirmDeleteSelected}
                disabled={deleteMut.isPending}
                className="inline-flex items-center gap-1.5 rounded-md border border-rose-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {deleteMut.isPending ? "Deleting…" : "Delete selected"}
              </button>
            )}
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {rows.length > 0 && (<>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              {isAdmin && (
                <Th className="w-10">
                  <input
                    type="checkbox"
                    checked={pageSelState === "all"}
                    ref={(el) => {
                      if (el) el.indeterminate = pageSelState === "some";
                    }}
                    onChange={handlePageToggle}
                    aria-label={pageSelState === "none" ? "Select all routes on this page" : "Clear selection on this page"}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                </Th>
              )}
              <Th>Date</Th>
              <Th>Name</Th>
              <Th>Driver</Th>
              <Th>Stops</Th>
              <Th>Distance</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageRows.map((r) => (
              <tr
                key={r.id}
                className={`hover:bg-slate-50 ${selectedIds.has(r.id) ? "bg-sky-50/60" : ""}`}
                onClick={(e) => {
                  if (!isAdmin) {
                    navigate(`/routes/${r.id}`);
                    return;
                  }
                  const target = e.target as HTMLElement;
                  // Don't hijack clicks on the row link or checkbox.
                  if (target.closest("a") || target.closest("input[type=checkbox]")) return;
                  handleRowClick(r.id, e.shiftKey);
                }}
              >
                {isAdmin && (
                  <Td className="w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(r.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(r.id, e.shiftKey);
                      }}
                      onChange={() => { /* handled in onClick to capture shiftKey */ }}
                      aria-label={`Select route ${r.name}`}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                  </Td>
                )}
                <Td>{format(parseISO(r.route_date), "MMM d, yyyy")}</Td>
                <Td>
                  <Link to={`/routes/${r.id}`} className="font-medium text-brand-700 hover:underline">
                    {r.name}
                  </Link>
                </Td>
                <Td className="text-slate-600">
                  {r.is_pool ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                      Team pool
                    </span>
                  ) : (
                    r.assigned_to_name ?? <span className="italic text-slate-400">unassigned</span>
                  )}
                </Td>
                <Td className="text-slate-700">{r.stop_count}</Td>
                <Td className="text-slate-700">{r.total_distance_m != null ? formatMiles(r.total_distance_m) : "—"}</Td>
                <Td><StatusBadge status={r.status} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
        <span>
          {(page * pageSize + 1).toLocaleString()}–
          {Math.min((page + 1) * pageSize, total).toLocaleString()} of {total.toLocaleString()}
        </span>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-slate-500">
            Per page
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value) as PageSize); setPage(0); }}
              className="rounded-md border-slate-300 py-1 pl-2 pr-7 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
      </>)}
    </div>
  );
}

function FilterBar({
  driverFilter,
  onChange,
  statusFilter,
  onStatusChange,
  members,
}: {
  driverFilter: RouteAssigneeFilter;
  onChange: (next: RouteAssigneeFilter) => void;
  statusFilter: RouteStatus | "all";
  onStatusChange: (next: RouteStatus | "all") => void;
  members: Array<{ id: string; fullName: string; role: string }>;
}) {
  const value = driverFilter ?? "all";
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm">
      <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-600">
        Driver
        <select
          value={value}
          onChange={(e) => onChange(e.target.value as RouteAssigneeFilter)}
          className="rounded-md border-slate-300 py-1 pl-2 pr-7 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
        >
          <option value="all">All routes</option>
          <option value="pool">Team pool (unclaimed)</option>
          <option value="unassigned">Unassigned (not pool)</option>
          {members.length > 0 && <option disabled>──────────</option>}
          {members.map((m) => {
            // Suffix non-driver roles so the filter is clear; bare driver
            // names stay un-suffixed for brevity. Mirrors UserMenu.
            const suffix = m.role === "admin_user" ? " (admin)" : m.role === "route_manager" ? " (route mgr)" : "";
            return (
              <option key={m.id} value={m.id}>
                {m.fullName}{suffix}
              </option>
            );
          })}
        </select>
      </label>
      <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-600">
        Status
        <select
          value={statusFilter}
          onChange={(e) => onStatusChange(e.target.value as RouteStatus | "all")}
          className="rounded-md border-slate-300 py-1 pl-2 pr-7 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </label>
      {(value !== "all" || statusFilter !== "all") && (
        <button
          type="button"
          onClick={() => { onChange("all"); onStatusChange("all"); }}
          className="text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`whitespace-nowrap px-4 py-3 text-sm ${className}`}>{children}</td>;
}
